
import { Dispatch, SetStateAction } from "react";
import { StartMainContainer } from "./StartMainContainer";
import { GamePlayContainer } from "./GamePlayContainer";
import { GameResultContainer } from "./GameResultContainer";
import type { DailyPuzzle } from "@/lib/types/game";
import type { LogicRule } from "@/lib/game-utils";
import type { GameResult } from "@/hooks/useGameFlow";
import { markGameCompleted, saveGameResult, setViewingResults } from "@/lib/storage/local-storage";

interface GameControllerProps {
  userEmail: string | null;
  todaysPuzzle: DailyPuzzle | null;
  showGamePlay: boolean;
  setShowGamePlay: (show: boolean) => void;
  setViewingPreviousResults: Dispatch<SetStateAction<boolean>>;
  setCurrentGameResult: (result: GameResult) => void;
  onGameStart: (email: string) => void;
  setFilteredRules: Dispatch<SetStateAction<LogicRule[]>>;
  filteredRules: LogicRule[];
  hasCompletedToday: boolean;
  isLoading?: boolean;
  todayActual: string;
  onClearCompletedStatus?: () => void;
}

export function GameController({
  userEmail,
  todaysPuzzle,
  showGamePlay,
  setShowGamePlay,
  setViewingPreviousResults,
  setCurrentGameResult,
  onGameStart,
  setFilteredRules,
  filteredRules,
  hasCompletedToday,
  isLoading = false,
  todayActual,
  onClearCompletedStatus
}: GameControllerProps) {
  // This function properly initiates game play with email
  const handleStartGame = (email?: string) => {
    console.log("GamePlayResultView: handleStartGame called with email:", email || userEmail);
    
    if (hasCompletedToday) {
      console.log("GamePlayResultView: Cannot start game - already completed today's puzzle");
      return false;
    }
    
    if (email || userEmail) {
      const emailToUse = email || userEmail;
      console.log("GamePlayResultView: Starting game with email:", emailToUse);
      
      // Explicitly set showGamePlay to true to display the GamePlayContainer
      setShowGamePlay(true);
      
      // Make sure we're not viewing results mode
      setViewingPreviousResults(false);
      setViewingResults(false);
      
      // Pass the email up to parent components
      if (emailToUse) {
        console.log("GamePlayResultView: Calling onGameStart with email:", emailToUse);
        onGameStart(emailToUse);
      }
      
      return true;
    } else {
      console.error("GamePlayResultView: Cannot start game: no email available");
      return false;
    }
  };

  // If we're showing gameplay and have a puzzle, render the GamePlayContainer
  if (showGamePlay && todaysPuzzle) {
    console.log("GamePlayResultView: Rendering GamePlayContainer with puzzle:", todaysPuzzle.correctRule.name);
    return (
      <GamePlayContainer
        puzzle={todaysPuzzle}
        onComplete={(result, solveTime, selectedWords, selectedRule, rules) => {
          console.log("GamePlayResultView: Game completed with result:", result);
          
          // Ensure result is strictly "perfect" or "wrong"
          const finalResult: "perfect" | "wrong" = 
            result === "perfect" ? "perfect" : "wrong";
          
          const newGameResult: GameResult = {
            result: finalResult,
            solveTime,
            selectedWords,
            selectedRule,
            completedDate: todayActual
          };
          
          // Save the game result to state for passing to result screen
          setCurrentGameResult(newGameResult);
          
          // Exit gameplay mode
          setShowGamePlay(false);
          
          // Pass full information to filtered rules
          setFilteredRules(rules);
          
          // After completion, show results
          setViewingPreviousResults(true);
          
          // Set viewing results flag in localStorage
          setViewingResults(true);
          
          // Save the complete game result to localStorage for persistence
          saveGameResult(newGameResult);
        }}
      />
    );
  }

  // Only show start container if user has not completed today's puzzle
  return (
    <StartMainContainer
      userEmail={userEmail}
      onGameStart={handleStartGame}
      onViewPreviousResults={() => {
        setViewingPreviousResults(true);
        setViewingResults(true);
      }}
      hasCompletedToday={hasCompletedToday}
      isLoading={isLoading}
      onClearCompletedStatus={onClearCompletedStatus} 
    />
  );
}
